import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Flame, Trophy, Sparkles, Globe as GlobeIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { loadProgress, worldPoints } from "@/lib/progress";
import { worldCompletion } from "@/lib/progression";
import { cn } from "@/lib/utils";

/**
 * ProgressPill — compact right-cluster pill that consolidates the old
 * five separate header stat chips (day streak, stars, mastered, badges,
 * world-discovery %) into a single tap target.
 *
 * The pill itself always shows the two most-loved indicators (⭐ stars
 * + 🔥 streak) so kids get instant feedback without any extra taps.
 * Opening the popover reveals the full five-stat breakdown plus a
 * "View Passport" CTA so the child can jump into their trophy shelf.
 *
 * All numbers come from localStorage-backed `loadProgress()` so the
 * pill stays live across the app (no props required). We subscribe to
 * `we-progress-changed` + `we-active-child-changed` events so it
 * re-renders the moment the active child switches or a lesson awards
 * new stars/badges.
 */
export default function ProgressPill() {
  const [progress, setProgress] = useState(() => loadProgress());

  useEffect(() => {
    const refresh = () => setProgress(loadProgress());
    window.addEventListener("we-progress-changed", refresh);
    window.addEventListener("we-active-child-changed", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("we-progress-changed", refresh);
      window.removeEventListener("we-active-child-changed", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  const stars = progress?.stars || 0;
  const streak = progress?.streak || 0;
  const points = worldPoints(progress || {});
  const badges = progress?.badges?.length || 0;
  // "Mastered" = quizzes/lessons completed. Approximation from local
  // progress so no server round-trip is needed for the header pill.
  const mastered =
    (progress?.quizzesCompleted || 0) +
    (progress?.gamesWon || 0) +
    (progress?.coloringsCompleted || 0);
  const worldPct = worldCompletion(progress).pct;

  const rows = [
    { icon: Flame,    color: "text-orange-600", bg: "bg-orange-100", label: "Day streak",    value: `${streak}d`,           testid: "pill-stat-streak" },
    { icon: Star,     color: "text-yellow-700", bg: "bg-yellow-100", label: "Stars",         value: stars,                  testid: "pill-stat-stars" },
    { icon: Trophy,   color: "text-emerald-700", bg: "bg-emerald-100", label: "Mastered",    value: mastered,               testid: "pill-stat-mastered" },
    { icon: Sparkles, color: "text-fuchsia-700", bg: "bg-fuchsia-100", label: "Badges",      value: badges,                 testid: "pill-stat-badges" },
    { icon: GlobeIcon,color: "text-sky-700",    bg: "bg-sky-100",     label: "World map",    value: `${worldPct}%`,         testid: "pill-stat-world" },
  ];

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="header-progress-pill"
          aria-label={`Progress: ${stars} stars, ${streak} day streak, ${mastered} mastered, ${badges} badges, ${worldPct}% of world explored. Tap for details.`}
          title="Your progress"
          className={cn(
            "inline-flex items-center gap-1.5 h-10 px-2.5 rounded-full border-[3px] border-slate-900",
            "bg-gradient-to-b from-yellow-200 to-amber-300",
            "shadow-[0_3px_0_0_rgba(15,23,42,1)] hover:translate-y-[1px] hover:shadow-[0_2px_0_0_rgba(15,23,42,1)]",
            "active:translate-y-[2px] transition-transform font-bold text-sm shrink-0",
          )}
        >
          <span className="inline-flex items-center gap-1">
            <Star size={14} strokeWidth={3} className="text-yellow-700 fill-yellow-500" />
            <span className="tabular-nums" data-testid="pill-compact-stars">{stars}</span>
          </span>
          <span aria-hidden className="h-4 w-px bg-slate-900/30" />
          {/* Reward simplification (MOAT): Stars = effort, Points = learning. Streak stays in the popover only. */}
          <span className="inline-flex items-center gap-1" title="Points">
            <span aria-hidden className="text-[12px]">🎯</span>
            <span className="tabular-nums" data-testid="pill-compact-points">{points}</span>
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        data-testid="progress-pill-popover"
        className="w-72 border-[3px] border-slate-900 rounded-2xl shadow-[0_6px_0_0_rgba(15,23,42,1)] bg-white p-3"
      >
        <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 px-1 pb-2">
          My progress
        </p>
        <ul className="space-y-1.5">
          {rows.map((r) => {
            const Icon = r.icon;
            return (
              <li
                key={r.label}
                data-testid={r.testid}
                className="flex items-center gap-2.5 px-2 py-1.5 rounded-xl border-2 border-slate-200"
              >
                <span className={cn("inline-flex items-center justify-center h-8 w-8 rounded-full border-2 border-slate-900", r.bg)}>
                  <Icon size={16} strokeWidth={3} className={r.color} />
                </span>
                <span className="flex-1 font-bold text-sm text-slate-900">{r.label}</span>
                <span className="tabular-nums font-heading font-black text-slate-900">{r.value}</span>
              </li>
            );
          })}
        </ul>
        <Link
          to="/passport"
          data-testid="progress-pill-passport-link"
          className="mt-3 flex items-center justify-center gap-1.5 h-10 rounded-xl border-[2.5px] border-slate-900 bg-amber-300 font-bold text-sm shadow-[0_3px_0_0_rgba(15,23,42,1)] hover:-translate-y-0.5 hover:bg-amber-400 transition-all"
        >
          <span aria-hidden>🎒</span>
          <span>View Passport</span>
          <span aria-hidden>→</span>
        </Link>
      </PopoverContent>
    </Popover>
  );
}
